import { MOCKS_ENABLED, MOCK_LATENCY_MS, resolveMockOperation } from '@/mocks/graphqlMockHandlers';
import { SUBSCRIPTION_MOCK_ENABLED, resolveSubscriptionMock } from '@/mocks/subscriptionMock';
import { TREND_MOCK_ENABLED, resolveTrendMock } from '@/mocks/trendMock';
import { GraphQLResponseError } from '@/shared/lib/graphqlResponseError';

import { API_CONFIG, AUTH_CONFIG } from '../config/api';

interface GraphQLResponse<T = any> {
  data?: T;
  errors?: Array<{
    message: string;
    locations?: Array<{ line: number; column: number }>;
    path?: Array<string | number>;
    extensions?: {
      code?: string;
      [key: string]: any;
    };
  }>;
}

interface GraphQLRequest {
  query: string;
  variables?: Record<string, any>;
  operationName?: string;
}

// Type pour la fonction de récupération de token
type TokenRefreshFunction = () => Promise<string | null>;

class GraphQLClient {
  private endpoint: string;
  private defaultHeaders: Record<string, string>;
  private tokenRefreshFn: TokenRefreshFunction | null = null;

  constructor(endpoint: string, defaultHeaders: Record<string, string> = {}) {
    this.endpoint = endpoint;
    this.defaultHeaders = defaultHeaders;
  }

  // Méthode pour enregistrer la fonction de rafraîchissement de token
  setTokenRefreshFunction(fn: TokenRefreshFunction) {
    this.tokenRefreshFn = fn;
  }

  // Méthode pour supprimer le token d'authentification
  clearAuthToken() {
    localStorage.removeItem(AUTH_CONFIG.tokenKey);
  }

  async request<T = any>(request: GraphQLRequest, headers: Record<string, string> = {}): Promise<T> {
    return await this.makeRequest(request, headers);
  }

  private async makeRequest<T = any>(request: GraphQLRequest, headers: Record<string, string> = {}): Promise<T> {
    const token = await this.getAuthToken();

    const response = await fetch(this.endpoint, {
      method: 'POST',
      headers: {
        ...this.defaultHeaders,
        ...(token && { Authorization: `Bearer ${token}` }),
        ...headers,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result: GraphQLResponse<T> = await response.json();

    if (result.errors && result.errors.length > 0) {
      const error = result.errors[0];

      // Vérifier si c'est une erreur d'autorisation JWT expiré
      const isAuthError =
        error.message.includes('Unauthorized') ||
        error.message.includes('Authentication required') ||
        error.extensions?.code === 'UNAUTHENTICATED';

      if (isAuthError) {
        // Nettoyer le token si erreur d'auth (le refresh aurait déjà été tenté dans getAuthToken)
        this.setAuthToken(null);
        throw new Error('Authentication required. Please log in again.');
      }

      throw new GraphQLResponseError(error.message, error.extensions?.code);
    }

    if (!result.data) {
      throw new Error('No data received from GraphQL endpoint');
    }

    return result.data;
  }

  private async getAuthToken(): Promise<string | null> {
    if (typeof window === 'undefined') return null;

    let token = localStorage.getItem(AUTH_CONFIG.tokenKey);
    if ((!token || this.isTokenExpired(token)) && this.tokenRefreshFn) {
      token = await this.tokenRefreshFn();
    }
    return token;
  }

  private isTokenExpired(token: string): boolean {
    try {
      // Décoder le payload JWT (partie entre les deux points)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      // Vérifier si le token expire dans les 30 prochaines secondes (buffer de sécurité)
      return payload.exp && payload.exp - 30 < currentTime;
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      // En cas d'erreur de décodage, considérer le token comme expiré
      return true;
    }
  }

  private setAuthToken(token: string | null): void {
    if (typeof window === 'undefined') return;

    if (token) {
      localStorage.setItem(AUTH_CONFIG.tokenKey, token);
    } else {
      localStorage.removeItem(AUTH_CONFIG.tokenKey);
    }
  }
}

// Export singleton instance
const clientInstance = new GraphQLClient(API_CONFIG.endpoint, API_CONFIG.headers);

// Export function compatible with codegen expectations
export const graphqlClient =
  <T = any, V = Record<string, any>>(query: string, variables?: V, headers?: Record<string, string>) =>
  async () => {
    // Mode maquette : les opérations connues sont résolues en mémoire
    // (VITE_USE_MOCKS=true), les autres passent au réseau réel.
    if (MOCKS_ENABLED) {
      const mock = resolveMockOperation(query, variables as Record<string, any>);
      if (mock !== undefined) {
        await new Promise((resolve) => setTimeout(resolve, MOCK_LATENCY_MS));
        return mock as T;
      }
    }
    // Mock CIBLÉ Trend (VITE_MOCK_TREND=true) : seule GetTrendList est
    // interceptée — le back n'a pas encore de run publié, tout le reste
    // de l'app continue de parler au vrai serveur.
    if (TREND_MOCK_ENABLED) {
      const mock = resolveTrendMock(query, variables as Record<string, any>);
      if (mock !== undefined) {
        await new Promise((resolve) => setTimeout(resolve, MOCK_LATENCY_MS));
        return mock as T;
      }
    }
    // Mock CIBLÉ abonnements (VITE_MOCK_SUBSCRIPTION=trial|debutant|classique|pro) :
    // seules les LECTURES d'abonnement/quotas sont interceptées pour tester
    // l'app dans chaque état de plan — tout le reste parle au vrai serveur.
    if (SUBSCRIPTION_MOCK_ENABLED) {
      const mock = resolveSubscriptionMock(query);
      if (mock !== undefined) {
        await new Promise((resolve) => setTimeout(resolve, MOCK_LATENCY_MS));
        return mock as T;
      }
    }
    return clientInstance.request<T>({ query, variables: variables as Record<string, any> }, headers || {});
  };

// Export the instance for direct access if needed
export { clientInstance };
